import React, { FC, useState, useEffect } from 'react'
import { CombinedImageItem, AnalyzeRecolorResponse, RecolorModalStep } from '../types'

interface RecolorModalProps {
  visible: boolean
  image: CombinedImageItem | null
  onClose: () => void
  onAnalyze: (imageUrl: string, color: string, description: string) => Promise<void>
  onSave: (imageBase64: string, color: string) => Promise<string | null>
  onApplyTransformation: (
    imageUrl: string,
    targetColor: string,
    bounds: { x: number; y: number; width: number; height: number }
  ) => Promise<string | null>
  analysis: AnalyzeRecolorResponse['data'] | null
  loading: boolean
  error: string | null
}

/**
 * Modal de Recolor
 * Fluxo: Selecionar Cor → Analisar → Preview → Transformar & Salvar
 */
export const RecolorModal: FC<RecolorModalProps> = ({
  visible,
  image,
  onClose,
  onAnalyze,
  onSave,
  onApplyTransformation,
  analysis,
  loading,
  error,
}) => {
  const [selectedColor, setSelectedColor] = useState('#FF0000')
  const [description, setDescription] = useState('')
  const [step, setStep] = useState<RecolorModalStep>(RecolorModalStep.SELECT_COLOR)
  const [savedUrl, setSavedUrl] = useState<string | null>(null)
  const [transformedPreview, setTransformedPreview] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => {
    if (!visible) {
      setStep(RecolorModalStep.SELECT_COLOR)
      setSelectedColor('#FF0000')
      setDescription('')
      setSavedUrl(null)
      setTransformedPreview(null)
      setPreviewLoading(false)
    }
  }, [visible])

  const handleAnalyze = async () => {
    if (!image) return

    setStep(RecolorModalStep.ANALYZING)
    try {
      await onAnalyze(image.imageUrl, selectedColor, description)
      setStep(RecolorModalStep.ANALYZED)
      setTransformedPreview(null) // Reset preview quando analisar novamente
    } catch (err) {
      setStep(RecolorModalStep.SELECT_COLOR)
    }
  }

  // ✅ NOVO: Gerar preview da imagem transformada
  const handleGeneratePreview = async () => {
    if (!image || !analysis) return

    setPreviewLoading(true)
    try {
      const preview = await onApplyTransformation(
        image.imageUrl,
        selectedColor,
        analysis.bounds
      )
      if (preview) {
        setTransformedPreview(preview)
      }
    } catch (err) {
      console.error('[handleGeneratePreview] Error:', err)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSave = async () => {
    if (!image || !selectedColor || !analysis) return

    setStep(RecolorModalStep.SAVING)
    try {
      // ✅ Se já tem preview, usar; senão gerar
      let imageToSave = transformedPreview
      if (!imageToSave) {
        imageToSave = await onApplyTransformation(
          image.imageUrl,
          selectedColor,
          analysis.bounds
        )
      }

      if (!imageToSave) {
        throw new Error('Falha ao transformar imagem')
      }

      // ✅ PASSO 2: Salvar imagem transformada no backend
      const url = await onSave(imageToSave, selectedColor)
      if (url) {
        setSavedUrl(url)
        setStep(RecolorModalStep.SAVED)
      } else {
        setStep(RecolorModalStep.ANALYZED)
      }
    } catch (err) {
      console.error('[handleSave] Error:', err)
      setStep(RecolorModalStep.ANALYZED)
    }
  }

  if (!image) return null

  return (
    <>
      {/* BACKDROP */}
      {visible && (
        <div
          className='modal-backdrop fade show'
          onClick={onClose}
          style={{ zIndex: 1040 }}
        />
      )}

      {/* MODAL */}
      <div
        className={`modal fade ${visible ? 'show' : ''}`}
        style={{
          display: visible ? 'block' : 'none',
          zIndex: 1050,
        }}
        tabIndex={-1}
      >
        <div className='modal-dialog modal-lg modal-dialog-centered'>
          <div className='modal-content'>
            {/* HEADER */}
            <div className='modal-header'>
              <h5 className='modal-title'>
                <i className='bi bi-palette me-2'></i>
                Recolorizar Imagem
              </h5>
              <button
                type='button'
                className='btn-close'
                onClick={onClose}
                disabled={loading}
              />
            </div>

            {/* BODY */}
            <div className='modal-body'>
              {error && (
                <div className='alert alert-danger' role='alert'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  {error}
                </div>
              )}

              <div className='row'>
                {/* IMAGEM ORIGINAL */}
                <div className={analysis || transformedPreview ? 'col-md-6' : 'col-12'}>
                  <h6>Imagem Original</h6>
                  <img
                    src={image.imageUrl}
                    alt='Original'
                    className='img-fluid rounded'
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).src = 'https://placehold.co/300x300?text=Erro'
                    }}
                  />
                </div>

                {/* PREVIEW DA TRANSFORMAÇÃO OU ANÁLISE */}
                {(analysis || transformedPreview) && (
                  <div className='col-md-6'>
                    {/* Se tem preview transformado, mostrar imagem */}
                    {transformedPreview ? (
                      <>
                        <h6>
                          <i className='bi bi-check-circle me-2 text-success'></i>
                          Imagem Recolorizada
                        </h6>
                        <img
                          src={transformedPreview}
                          alt='Recolored'
                          className='img-fluid rounded'
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                        />
                        <small className='text-success d-block mt-2'>
                          <i className='bi bi-check me-1'></i>
                          Cor aplicada com sucesso. Pronto para salvar!
                        </small>
                      </>
                    ) : (
                      <>
                        <h6>Resultado da Análise</h6>
                        <div className='bg-light rounded p-3' style={{ minHeight: '300px', overflowY: 'auto' }}>
                          <p className='mb-2'>
                            <strong>Confiança:</strong>
                            <br />
                            <span className='badge bg-info'>{(analysis?.confidence ? (analysis.confidence * 100).toFixed(1) : '0')}%</span>
                          </p>
                          <p className='mb-2'>
                            <strong>Descrição:</strong>
                            <br />
                            <small className='text-muted'>{analysis?.description || '-'}</small>
                          </p>
                          <p className='mb-2'>
                            <strong>Análise Técnica:</strong>
                            <br />
                            <small className='text-muted'>{analysis?.analysis || 'Análise não disponível'}</small>
                          </p>
                          <p className='mb-0'>
                            <strong>Cores Sugeridas:</strong>
                            <br />
                            <small className='text-muted'>{analysis?.suggestedColors || 'N/A'}</small>
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* BOTÃO: Visualizar Resultado (após análise, antes de preview) */}
              {analysis && !transformedPreview && step === RecolorModalStep.ANALYZED && (
                <div className='mt-3'>
                  <button
                    type='button'
                    className='btn btn-info w-100 mb-2'
                    onClick={handleGeneratePreview}
                    disabled={previewLoading || loading}
                  >
                    {previewLoading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                        Gerando preview...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-image me-2'></i>
                        Visualizar Resultado
                      </>
                    )}
                  </button>
                  <small className='text-muted d-block'>
                    Clique para gerar preview da imagem recolorizada
                  </small>
                </div>
              )}

              {/* CONTROLES */}
              {step === RecolorModalStep.SELECT_COLOR && (
                <div className='mt-4'>
                  <div className='mb-3'>
                    <label className='form-label'>Cor Alvo</label>
                    <div className='input-group'>
                      <input
                        type='color'
                        className='form-control form-control-color'
                        value={selectedColor}
                        onChange={(e) => setSelectedColor(e.target.value)}
                        style={{ cursor: 'pointer', width: '80px' }}
                        disabled={loading}
                      />
                      <input
                        type='text'
                        className='form-control'
                        value={selectedColor}
                        onChange={(e) => setSelectedColor(e.target.value)}
                        placeholder='#FF0000'
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className='mb-3'>
                    <label className='form-label'>Descrição da Peça</label>
                    <input
                      type='text'
                      className='form-control'
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder='Ex: blue shirt, red pants, white dress'
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* RESULTADO SALVE */}
              {step === RecolorModalStep.SAVED && savedUrl && (
                <div className='alert alert-success mt-3'>
                  <i className='bi bi-check-circle me-2'></i>
                  Imagem recolorida salva com sucesso!
                  <br />
                  <small>URL: {savedUrl}</small>
                </div>
              )}

              {/* LOADING STATE */}
              {(step === RecolorModalStep.ANALYZING || step === RecolorModalStep.SAVING) && (
                <div className='text-center mt-4'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Processando...</span>
                  </div>
                  <p className='mt-2 text-muted'>
                    {step === RecolorModalStep.ANALYZING ? 'Analisando imagem...' : 'Salvando imagem...'}
                  </p>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className='modal-footer'>
              <button
                type='button'
                className='btn btn-secondary'
                onClick={onClose}
                disabled={loading}
              >
                Fechar
              </button>

              {step === RecolorModalStep.SELECT_COLOR && (
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={handleAnalyze}
                  disabled={loading || !selectedColor || !description}
                >
                  {loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Analisando...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-search me-2'></i>
                      Analisar
                    </>
                  )}
                </button>
              )}

              {step === RecolorModalStep.ANALYZED && (
                <button
                  type='button'
                  className={`btn ${transformedPreview ? 'btn-success' : 'btn-primary'}`}
                  onClick={transformedPreview ? handleSave : handleGeneratePreview}
                  disabled={loading || previewLoading}
                >
                  {loading || previewLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Processando...
                    </>
                  ) : transformedPreview ? (
                    <>
                      <i className='bi bi-download me-2'></i>
                      Salvar Imagem
                    </>
                  ) : (
                    <>
                      <i className='bi bi-image me-2'></i>
                      Visualizar Resultado
                    </>
                  )}
                </button>
              )}

              {step === RecolorModalStep.SAVED && (
                <button type='button' className='btn btn-primary' onClick={onClose}>
                  <i className='bi bi-check me-2'></i>
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
